import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Folder, GitBranch, Loader2, ChevronDown, Star, X, Settings, FileArchive, Search, Compass } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useProjects } from '@/hooks/useProjects';
import { useProjectSessionStatus } from '@/hooks/useProjectSessionStatus';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useProjectsManager } from '@/hooks/useProjectsManager';
import type { Project } from '@/lib/ProjectsManager';
import { cn } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { AfterHoursLogo } from '@/components/AfterHoursLogo';
import { ZipImportDialog } from '@/components/ZipImportDialog';
import { ProjectContextMenu } from '@/components/ProjectContextMenu';
import { LoginArea } from '@/components/auth/LoginArea';

interface ProjectItemProps {
  project: Project;
  isSelected: boolean;
  onSelect: (project: Project) => void;
  isFavorite?: boolean;
  onProjectDeleted?: () => void;
  showFavoriteStar?: boolean;
}

const ProjectItem: React.FC<ProjectItemProps> = ({ project, isSelected, onSelect, isFavorite, onProjectDeleted, showFavoriteStar = false }) => {
  const navigate = useNavigate();
  const { hasRunningSessions } = useProjectSessionStatus(project.id);

  const handleClick = () => {
    onSelect(project);
    navigate(`/project/${project.id}`);
  };

  return (
    <ProjectContextMenu project={project} onProjectDeleted={onProjectDeleted}>
      <button
        onClick={handleClick}
        className={cn(
          "w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm text-sidebar-foreground transition-colors hover:bg-sidebar-accent/70",
          isSelected && "bg-sidebar-accent"
        )}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {hasRunningSessions ? (
            <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-blue-500" />
          ) : (
            <Folder className="h-4 w-4 flex-shrink-0 text-primary" />
          )}
          <span className="truncate">{project.name}</span>
        </div>
        {showFavoriteStar && isFavorite && (
          <Star className="h-4 w-4 flex-shrink-0 text-yellow-500 fill-current" />
        )}
      </button>
    </ProjectContextMenu>
  );
};

ProjectItem.displayName = 'ProjectItem';

interface ProjectSidebarProps {
  selectedProject: Project | null;
  onSelectProject: (project: Project | null) => void;
  className?: string;
  onClose?: () => void;
  hideHeader?: boolean;
  isDesktopDrawer?: boolean;
  onDropdownOpenChange?: (open: boolean) => void;
}

export function ProjectSidebar({
  selectedProject,
  onSelectProject,
  className,
  onClose,
  hideHeader = false,
  isDesktopDrawer = false,
  onDropdownOpenChange
}: ProjectSidebarProps) {
  const queryClient = useQueryClient();
  const { data: projects = [], isLoading, error } = useProjects();
  const [favorites] = useLocalStorage<string[]>('project-favorites', []);
  const [searchQuery, setSearchQuery] = useState('');
  const [zipDialogOpen, setZipDialogOpen] = useState(false);
  const projectsManager = useProjectsManager();
  const navigate = useNavigate();

  // Filter projects based on search query
  const filteredProjects = useMemo(() => {
    if (!searchQuery.trim()) return projects;
    const query = searchQuery.toLowerCase();
    return projects.filter(project =>
      project.name.toLowerCase().includes(query) ||
      project.id.toLowerCase().includes(query)
    );
  }, [projects, searchQuery]);

  // Separate favorites and non-favorites
  const favoriteProjects = useMemo(() => {
    return filteredProjects.filter(p => favorites.includes(p.id));
  }, [filteredProjects, favorites]);

  const allProjects = useMemo(() => {
    return filteredProjects.filter(p => !favorites.includes(p.id));
  }, [filteredProjects, favorites]);

  // Custom navigate function that closes sidebar on mobile
  const navigateAndClose = useCallback((path: string) => {
    // Navigate first, then close after a small delay to ensure navigation completes
    navigate(path);
    if (onClose) {
      setTimeout(() => {
        onClose();
      }, 0);
    }
  }, [navigate, onClose]);

  const handleNewProject = () => {
    onSelectProject(null);
    navigateAndClose('/');
  };

  const handleZipImport = async (file: File, overwrite?: boolean, projectId?: string) => {
    const project = await projectsManager.importProjectFromZip(file, projectId, overwrite);
    queryClient.invalidateQueries({ queryKey: ['projects'] });
    onSelectProject(project);
    navigateAndClose(`/project/${project.id}`);
  };

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['projects'] });
  }, [favorites, queryClient]);

  if (error) {
    console.error('Failed to load projects:', error);
  }

  return (
    <div className={cn("flex flex-col h-full bg-sidebar", className)}>
      {/* Header */}
      {!hideHeader && (
        <div className="pt-safe">
          <div className="flex items-center justify-between px-4 h-12 border-b border-sidebar-border">
            <button
              onClick={() => navigateAndClose('/')}
              className="flex items-center gap-2 pr-2 hover:opacity-80 transition-opacity"
            >
              <AfterHoursLogo className="w-6 h-6" />
              <span className="text-lg font-bold text-primary">AfterHours</span>
            </button>
            {onClose && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={onClose}
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="px-3 py-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9 text-sm bg-sidebar-accent/50 border-sidebar-border"
          />
        </div>
      </div>

      {/* New Project Button with Dropdown */}
      <div className="px-3 pb-3">
        <div className="relative rounded-xl bg-primary hover:bg-primary/90 shadow-md hover:shadow-lg transition-all duration-200">
          <div className="flex">
            <button
              onClick={handleNewProject}
              className="flex-1 flex items-center justify-center px-4 py-2.5 text-sm font-medium text-primary-foreground rounded-l-xl hover:bg-white/10 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Project
            </button>
            <div className="w-px bg-primary-foreground/20" />
            <DropdownMenu onOpenChange={onDropdownOpenChange}>
              <DropdownMenuTrigger asChild>
                <button className="px-3 py-2.5 text-primary-foreground hover:bg-white/10 rounded-r-xl transition-colors">
                  <ChevronDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  className="flex items-center gap-2 cursor-pointer"
                  onSelect={() => navigateAndClose('/clone')}
                >
                  <GitBranch className="h-4 w-4" />
                  Import Repository
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="flex items-center gap-2 cursor-pointer"
                  onSelect={() => setZipDialogOpen(true)}
                >
                  <FileArchive className="h-4 w-4" />
                  Import ZIP
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Projects List */}
      <div className="flex-1 overflow-y-auto px-3">
        {/* Explore link - only show on mobile (above Favorites) */}
        {!isDesktopDrawer && (
          <div className="mb-4">
            <Button
              variant="ghost"
              size="sm"
              className="w-full h-9 justify-start gap-2 text-sidebar-foreground hover:bg-sidebar-accent/70"
              onClick={() => navigateAndClose('/explore')}
            >
              <Compass className="h-4 w-4" />
              Explore
            </Button>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full rounded-lg" />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Folder className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No projects yet</p>
            <p className="text-xs">Create your first project to get started</p>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No projects found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Favorites Section */}
            {favoriteProjects.length > 0 && (
              <div>
                <div className="px-3 py-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                  Favorites
                </div>
                <div className="space-y-0.5">
                  {favoriteProjects.map((project) => (
                    <ProjectItem
                      key={project.id}
                      project={project}
                      isSelected={selectedProject?.id === project.id}
                      onSelect={onSelectProject}
                      isFavorite={true}
                      showFavoriteStar={true}
                      onProjectDeleted={() => {
                        if (selectedProject?.id === project.id) {
                          onSelectProject(null);
                        }
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* All Apps Section */}
            {allProjects.length > 0 && (
              <div>
                <div className="px-3 py-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                  All Apps
                </div>
                <div className="space-y-0.5">
                  {allProjects.map((project) => (
                    <ProjectItem
                      key={project.id}
                      project={project}
                      isSelected={selectedProject?.id === project.id}
                      onSelect={onSelectProject}
                      isFavorite={false}
                      onProjectDeleted={() => {
                        if (selectedProject?.id === project.id) {
                          onSelectProject(null);
                        }
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-sidebar-border pb-safe mt-auto">
        {/* User Section */}
        <div className="px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <LoginArea className="flex-1 min-w-0" />
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => navigateAndClose('/settings')}
              aria-label="Settings"
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* ZIP Import Dialog */}
      <ZipImportDialog
        onImport={handleZipImport}
        open={zipDialogOpen}
        onOpenChange={setZipDialogOpen}
      />
    </div>
  );
}
